import { useContext, useEffect, useState } from 'react';
import {
	Box,
	Typography,
	Button,
	TextField,
	Avatar,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import UserContext from '../Contexts/UserContext';
import { default as axios } from '../axiosConfig';
import PeopleProfileUpload from './PeopleProfileUpload';
import { API_ChangePassword, API_SetUserProfilePictureUrl, API_UpdateUserName } from '../Assets/ApiUrlContainer';
import PasswordStrengthMeter from './PasswordStrengthMeter'; // Import the new component

const SettingsPage = () => {
	const currentUser = useContext(UserContext);
	const [user, setUser] = useState(null);
	const [openPhotoUpload, setOpenPhotoUpload] = useState(false);

	// State for Change Password Modal
	const [openDialog, setOpenDialog] = useState(false);
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');

	useEffect(() => {
		setUser(currentUser.user);
	}, [currentUser, setUser]);

	const handleChangeFirstName = (event) => {
		setUser({
			...user,
			FirstName: event.target.value,
		});
	};

	const handleChangeLastName = (event) => {
		setUser({
			...user,
			LastName: event.target.value,
		});
	};

	// Function to handle opening the dialog
	const handleOpenDialog = () => {
		setMessage('');
		setError('');
		setCurrentPassword('');
		setNewPassword('');
		setConfirmPassword('');

		if (user.IsGoogleUser) {
			window.open('https://myaccount.google.com/signinoptions/password', '_');
			return;
		}

		setOpenDialog(true);
	};

	// Function to handle closing the dialog
	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	// Function to handle password change
	const handleChangePassword = async () => {
		if (!currentPassword || !newPassword || !confirmPassword) {
			setError('All fields are required.');
			return;
		}

		try {
			const response = await axios.post(API_ChangePassword, {
				currentPassword,
				newPassword,
				confirmPassword,
			});

			setMessage(response.data.message);
			setError('');
			setCurrentPassword('');
			setNewPassword('');
			setConfirmPassword('');
			setTimeout(handleCloseDialog, 2000);
		} catch (err) {
			setError(err.response?.data?.error || 'An error occurred.');
			setMessage('');
		}
	};

	const handleUploadSuccess = async (photoUrl) => {
		setOpenPhotoUpload(false);
		try {
			await axios.post(API_SetUserProfilePictureUrl, {
				email: currentUser.user.Email,
				isGoogleUser: currentUser.user.IsGoogleUser,
				photoUrl: photoUrl,
			});

			currentUser.setUser({
				...currentUser.user,
				AccountPictureUrl: photoUrl,
			});
		} catch (error) {
			console.error(error);
		}
	};

	const handleNameUpdate = async () => {
		try {
			await axios.post(API_UpdateUserName, {
				email: currentUser.user.Email,
				isGoogleUser: currentUser.user.IsGoogleUser,
				firstname: user.FirstName,
				lastname: user.LastName,
			});

			currentUser.setUser({
				...currentUser.user,
				FirstName: user.FirstName,
				LastName: user.LastName,
			});
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<div className='tgs-content'>
			<div className='settingsPage'>
				{user && (
					<Box sx={{ padding: '16px' }}>
						<Typography variant='h6' gutterBottom>
							Settings
						</Typography>
						<Typography variant='body2' color='textSecondary' gutterBottom>
							Customize your profile image, personal information and change your password.
						</Typography>

						{/* Profile Image Section */}
						<Box sx={{ display: 'flex', alignItems: 'center', marginY: '16px' }}>
							{!user.AccountPictureUrl && <Avatar sx={{ width: 60, height: 60 }}></Avatar>}
							{user.AccountPictureUrl && (
								<img className='userPhoto' src={user.AccountPictureUrl} alt='Profile' />
							)}

							{!openPhotoUpload && (
								<Button
									variant='contained'
									sx={{ marginLeft: '16px' }}
									onClick={() => setOpenPhotoUpload(true)}
								>
									Upload
								</Button>
							)}

							{openPhotoUpload && (
								<>
									<PeopleProfileUpload
										onUploadSuccess={(url) => handleUploadSuccess(url)}
										isFileUpload={false}
										customUploadBtnClass='googleBlueBtn'
									/>

									<button
										onClick={() => setOpenPhotoUpload(false)}
										className='cancelProfilePicUpload'
									>
										Cancel
									</button>
								</>
							)}
						</Box>

						{/* Personal Information Section */}
						<Box sx={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
							<TextField
								label='First Name'
								autoComplete='off'
								defaultValue={user.FirstName || ''}
								fullWidth
								onInput={handleChangeFirstName}
							/>
							<TextField
								label='Last Name'
								autoComplete='off'
								defaultValue={user.LastName || ''}
								fullWidth
								onInput={handleChangeLastName}
							/>
						</Box>
						<TextField
							label='Email'
							defaultValue={user.Email}
							fullWidth
							disabled
							helperText='Contact admin to change your email address: (TGSAdmin@tgspark.com).'
						/>

						<Box sx={{ marginTop: '16px' }}>
							<Button variant='contained' onClick={handleNameUpdate}>
								Update Name
							</Button>
						</Box>

						{/* Password Section */}
						<Box sx={{ marginTop: '16px' }}>
							<Button variant='contained' onClick={handleOpenDialog}>
								Change Password
							</Button>
							{/* <Typography variant='caption' display='block' sx={{ marginTop: '8px' }}>
								Last changed:
							</Typography> */}
						</Box>

						{/* Change Password Dialog */}
						<Dialog open={openDialog} onClose={handleCloseDialog}>
							<DialogTitle>Change Password</DialogTitle>
							<DialogContent>
								<TextField
									label='Current Password'
									type={showCurrentPassword ? 'text' : 'password'}
									fullWidth
									margin='normal'
									value={currentPassword}
									onChange={(e) => setCurrentPassword(e.target.value)}
									InputProps={{
										endAdornment: (
											<InputAdornment position='end'>
												<IconButton
													onClick={() => setShowCurrentPassword((prev) => !prev)}
													edge='end'
												>
													{showCurrentPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>

								<TextField
									label='New Password'
									type={showNewPassword ? 'text' : 'password'}
									fullWidth
									margin='normal'
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
									InputProps={{
										endAdornment: (
											<InputAdornment position='end'>
												<IconButton
													onClick={() => setShowNewPassword((prev) => !prev)}
													edge='end'
												>
													{showNewPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>

								{/* Password Strength Meter */}
								<PasswordStrengthMeter
									password={newPassword}
									firstName={user?.FirstName?.trim() || ''}
									lastName={user?.LastName?.trim() || ''}
								/>

								<TextField
									label='Confirm New Password'
									type={showConfirmPassword ? 'text' : 'password'}
									fullWidth
									margin='normal'
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									InputProps={{
										endAdornment: (
											<InputAdornment position='end'>
												<IconButton
													onClick={() => setShowConfirmPassword((prev) => !prev)}
													edge='end'
												>
													{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>

								{message && <Typography color='green'>{message}</Typography>}
								{error && <Typography color='red'>{error}</Typography>}
							</DialogContent>
							<DialogActions>
								<Button onClick={handleCloseDialog} color='secondary'>
									Cancel
								</Button>
								<Button onClick={handleChangePassword} color='primary'>
									Save
								</Button>
							</DialogActions>
						</Dialog>
					</Box>
				)}
			</div>
		</div>
	);
};

export default SettingsPage;
